"use client";

/**
 * SourcesMarquee — two counter-scrolling rows of favicons for sites currently
 * being read, above a live page count.
 *
 * Favicons come from /api/favicon (server-side proxy), never straight from a
 * third-party icon CDN — the visitor's browser must not be made to announce
 * which domains we index to someone else's service, and the strict CSP blocks
 * external hosts anyway.
 *
 * Falls back to a static roster if the live endpoint is unreachable, so the
 * section never renders empty.
 *
 * THREE THINGS THIS HAS TO GET RIGHT, none of which it used to:
 *
 * NO DOMAIN TWICE ON SCREEN. The rows were split by index out of whatever the
 * endpoint returned, and it returns "tim.com.br" and "www.tim.com.br" as two
 * records — which render as the same chip, so the same site appeared twice in
 * one row. Worse, when the tail was shorter than three the second row fell back
 * to `domains.slice(0, half)`, i.e. row A again. Deduplication is on the LABEL,
 * which is what a viewer actually compares, and the rows are built from one
 * ordered window so they cannot overlap by construction.
 *
 * NEW SOURCES OVER TIME. It fetched once on mount and then showed the same
 * fifteen names until the page was reloaded. It now keeps a pool of every
 * domain it has seen, re-polls, and advances a window through the pool so the
 * chips move on. It only re-renders when genuinely new domains arrive — the CSS
 * animation restarts whenever the children change, and rotating on a timer for
 * its own sake makes the marquee visibly stutter every few seconds.
 *
 * FAVICONS THAT ARE ONE COLOUR. A black glyph on transparency vanishes on the
 * dark ground and a white one vanishes on the light. Each icon is measured once
 * on load — mean saturation and mean luminance over its non-transparent pixels —
 * and a monochrome one is inverted in the theme that would otherwise swallow it.
 * Only near-greyscale icons qualify; a coloured logo is never touched.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";

const DOMAINS_URL = "/api/index/last-indexed";
const RATE_URL = "/api/index/read-rate";
/** Slow: this exists to refresh the roster, not to stream it. */
const POLL_MS = 30_000;
/** The three rates move only every five minutes, but the counter behind "since you opened this
 *  page" is read live, and it is what makes the strip visibly alive. Matches the endpoint's own
 *  20-second cache, so no poll lands on a value the server has not refreshed. */
const RATE_POLL_MS = 20_000;

/** Shown until live data arrives, and if it never does. Recognisable, stable. */
const FALLBACK = [
  "wikipedia.org", "arxiv.org", "nih.gov", "python.org", "who.int",
  "nature.com", "gov.uk", "europa.eu", "mit.edu", "reuters.com",
  "kubernetes.io", "mozilla.org", "acm.org", "un.org", "bbc.co.uk",
];

type Row = { domain: string; pages?: number };

/** What the viewer reads, and therefore what counts as a duplicate. */
const labelOf = (domain: string) => domain.replace(/^www\./, "").toLowerCase();

function faviconUrl(domain: string) {
  return `/api/favicon?d=${encodeURIComponent(domain)}`;
}

/**
 * A favicon that survives both themes.
 *
 * Measured on load rather than guessed: the icon is drawn to a 16×16 canvas and
 * its non-transparent pixels averaged. `sat` near zero means the icon carries no
 * colour of its own, and only then does luminance decide anything — a dark
 * greyscale mark is inverted on the dark ground, a light one on the light. The
 * image is same-origin (it comes through /api/favicon), so reading the canvas is
 * allowed; a cross-origin icon would taint it and throw, which the catch treats
 * as "leave it alone".
 */
function Favicon({ domain }: { domain: string }) {
  const [ink, setInk] = useState<"" | "dark" | "light">("");

  const measure = useCallback((img: HTMLImageElement) => {
    try {
      const n = 16;
      const c = document.createElement("canvas");
      c.width = n;
      c.height = n;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, n, n);
      const { data } = ctx.getImageData(0, 0, n, n);

      let lum = 0;
      let sat = 0;
      let seen = 0;
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 32) continue; // transparent padding says nothing about the mark
        const r = data[i] / 255;
        const g = data[i + 1] / 255;
        const b = data[i + 2] / 255;
        lum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        sat += Math.max(r, g, b) - Math.min(r, g, b);
        seen += 1;
      }
      if (seen < 8) return; // effectively blank — the 1×1 fallback lands here

      const meanLum = lum / seen;
      const meanSat = sat / seen;
      if (meanSat > 0.12) return; // a coloured logo is left exactly as it is
      if (meanLum < 0.22) setInk("dark");
      else if (meanLum > 0.86) setInk("light");
    } catch {
      /* Tainted or undecodable: showing it unmodified is the safe outcome. */
    }
  }, []);

  /* A CACHED IMAGE CAN FINISH BEFORE THE LISTENER EXISTS. `onLoad` only fires for a load that
     happens after React attaches it, and a favicon the browser already has decodes synchronously —
     so on every visit after the first, the icon would arrive `complete` and never be measured. The
     ref callback covers that case; `onLoad` covers the first visit. */
  const attach = useCallback((img: HTMLImageElement | null) => {
    if (img && img.complete && img.naturalWidth > 0) measure(img);
  }, [measure]);

  return (
    <img
      ref={attach}
      src={faviconUrl(domain)}
      alt=""
      width={18}
      height={18}
      loading="lazy"
      onLoad={(e) => measure(e.currentTarget)}
      className={
        "h-[18px] w-[18px] shrink-0 rounded-[3px] object-contain" +
        (ink === "dark" ? " fav-ink-dark" : ink === "light" ? " fav-ink-light" : "")
      }
    />
  );
}

function Chip({ domain }: { domain: string }) {
  const label = labelOf(domain);
  return (
    <div
      className="group flex shrink-0 items-center gap-2.5 rounded-full border hairline bg-card px-4 py-2"
      title={label}
    >
      <Favicon domain={domain} />
      <span className="font-mono text-[12px] text-foreground/70 whitespace-nowrap">{label}</span>
    </div>
  );
}

type Rate = {
  /** Durable counter, stepped once per five-minute snapshot. Backs the windowed rates. */
  fetched: number | null;
  /** Raw sum over nodes online now. Moves continuously, and is only ever used as a difference
   *  across the time this page has been open — see the note on the endpoint. */
  live: number | null;
  per_min: number | null;
  per_hour: number | null;
  per_day: number | null;
};

/** Eases from whatever is on screen to `value`, and never past it.
 *
 *  The distinction matters. Extrapolating forward from a rate would make the digits move more of
 *  the time, and every frame of that movement would be invented — the page would be asserting
 *  pages were read in a second nobody measured. This only ever travels BETWEEN two real readings,
 *  so the motion is a redraw of a measurement rather than a guess dressed as one.
 */
function useTween(value: number | null, ms = 900): number | null {
  const [n, setN] = useState<number | null>(null);
  const from = useRef<number | null>(null);
  useEffect(() => {
    if (value == null) return;

    /* FIRST VALUE IS PAINTED, NOT ANIMATED. Everything below runs inside requestAnimationFrame,
       which a browser does not run in a background tab — so a page opened in one (a cmd-click, a
       restored session) rendered an em dash over a figure it already had, and only filled in if
       the reader happened to switch to it. Animating from nowhere is also the wrong gesture: the
       motion is meant to show a number CHANGING, and the first reading has not changed. */
    if (from.current == null) {
      from.current = value;
      setN(value);
      return;
    }

    const start = from.current;
    const delta = value - start;
    if (delta === 0) {
      setN(value);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / ms, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(start + eased * delta);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return n;
}

function RateCell({
  value,
  label,
  decimals = 0,
}: {
  value: number | null;
  label: string;
  decimals?: number;
}) {
  const n = useTween(value);
  const text =
    n == null
      ? "—"
      : decimals
        ? n.toFixed(decimals)
        : Math.round(n).toLocaleString("en-US");
  return (
    <div className="text-center">
      <div className="display-text is-stat tabular-num whitespace-nowrap text-[clamp(1.3rem,3.4vw,1.9rem)]">
        {text}
      </div>
      <div className="mt-2 text-[10px] uppercase leading-none tracking-[0.16em] text-foreground/55">
        {label}
      </div>
    </div>
  );
}

/** Pages read per minute, hour and day, and the count read while you have been here.
 *
 *  WHICH COUNTER. The obvious one is what /indexing_in_progress shows, and it is the wrong one to
 *  publish: that gauge reads `pool/analytics.fetched`, which sums only the nodes online at that
 *  instant, so it falls when a machine leaves and resets when one restarts. Sampled twenty seconds
 *  apart it also swings hard — 1,237/min and 486/min were both true readings within minutes of each
 *  other on 16 September 2026. These figures come from the durable counter the snapshot loop
 *  accumulates from per-node forward movement instead, which is the same fix `stats:bw_total:*`
 *  already had, and each window is measured over itself rather than multiplied up from a shorter
 *  one. A window that is not there yet shows an em dash, never a zero.
 *
 *  The line underneath is the only part that is genuinely real-time, and it is real: the difference
 *  between the counter now and the counter when this page opened. That is a measurement of a window
 *  the reader defined by arriving, which is why it can be shown moving when a site-wide total
 *  cannot be shown at all.
 */
function ReadRate({ initial }: { initial?: Rate | null }) {
  /* Seeded by the server so the figures are in the HTML on first paint rather than arriving one
     column at a time. The client poll below still runs — this only removes the empty first frame. */
  const [rate, setRate] = useState<Rate | null>(initial ?? null);
  /* ACCUMULATED FORWARD MOVEMENT, not `now - first reading`.
     Held in refs, not state: they must never re-trigger the effect, and they are deliberately NOT
     persisted — the honest claim is "since you opened this page", and a stored baseline would
     quietly compare against some past visit.

     The naive version subtracted the first reading from the current one and re-baselined whenever
     the sum dipped. Both halves were wrong together: the live figure sums only the nodes online, so
     a machine leaving drops it, and re-baselining then DISCARDED everything counted so far. Seen on
     16 September 2026 — the line fell from 1,808 to 1,530 while the reader watched, which is the
     one thing a count of what has happened since you arrived must never do. Adding only forward
     movement makes it monotonic: a dip contributes nothing rather than something negative, so the
     count is honest about what it missed instead of pretending it un-happened. */
  const last = useRef<number | null>(null);
  const accrued = useRef(0);
  const [since, setSince] = useState<number | null>(null);

  useEffect(() => {
    let on = true;
    const pull = () =>
      fetch(RATE_URL, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: Rate | null) => {
          if (!on || !d) return;
          setRate(d);
          /* The LIVE sum, not the durable counter: this line exists to move, and the durable one
             only steps every five minutes. */
          if (typeof d.live === "number") {
            const prev = last.current;
            if (prev != null && d.live > prev) accrued.current += d.live - prev;
            last.current = d.live;
            if (prev != null) setSince(accrued.current);
          }
        })
        .catch(() => {});
    pull();
    const t = setInterval(pull, RATE_POLL_MS);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const sinceTween = useTween(since, 1800);
  const hasSince = sinceTween != null && sinceTween >= 1;

  /* Nothing read in the last fifteen minutes. Measured, not inferred: the counter is flat across
     consecutive snapshots rather than merely unreported. */
  const paused = rate?.per_min === 0;

  const cells = (
    [
      { value: rate?.per_min ?? null, label: "pages a minute", decimals: 1 },
      { value: rate?.per_hour ?? null, label: "pages an hour", decimals: 0 },
      { value: rate?.per_day ?? null, label: "pages a day", decimals: 0 },
    ] as const
  ).filter((c) => c.value != null);

  return (
    <div className="mt-8 flex flex-col items-center gap-4">
      {/* ONLY THE WINDOWS THAT EXIST. Each rate is measured over its own period and the endpoint
          withholds one it cannot cover, so after a deploy the hour arrives an hour later and the
          day a day later. Rendering a fixed three columns would stand two em dashes next to a real
          figure, which reads as broken rather than as honest — so the row is built from whatever is
          actually measurable and widens on its own as the history fills in. */}
      {cells.length > 0 ? (
        <div
          className={`grid w-full max-w-lg gap-4 sm:gap-6 ${
            cells.length === 1 ? "grid-cols-1" : cells.length === 2 ? "grid-cols-2" : "grid-cols-3"
          }`}
        >
          {cells.map((c) => (
            <RateCell key={c.label} value={c.value} label={c.label} decimals={c.decimals} />
          ))}
        </div>
      ) : null}

      {/* Rendered only once it has counted something. A line reading "0 pages read since you opened
          this page" is the worst thing this strip could say, and it is what the first few seconds
          would always show. */}
      {hasSince ? (
        <p className="text-[11px] text-foreground/45">
          <span className="font-mono tabular-num text-foreground/70">
            {Math.round(sinceTween).toLocaleString("en-US")}
          </span>{" "}
          pages read since you opened this page
        </p>
      ) : null}

      {/* SAY WHERE IT COMES FROM. The claim this strip makes is that these are real readings off a
          running fleet, and the only thing that makes that checkable is a link to the dashboard the
          same counters are drawn on.

          AND SAY WHEN IT IS NOT RUNNING. A pulsing "live" dot over a row reading 0.0 is the strip
          contradicting itself, and the reader is left to decide whether the fleet has stopped or the
          page has broken. A measured zero is a fact about the crawler, so it gets said out loud.
          `paused` is deliberately `=== 0` and not falsy: null is "we could not measure this window",
          which is the other thing entirely and must keep the ordinary badge. */}
      <a
        href="https://askfinz.ai/indexing_in_progress"
        target="_blank"
        rel="noopener"
        className="group inline-flex items-center gap-2 rounded-full border hairline bg-card px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-foreground/55 transition-colors hover:text-foreground/80"
      >
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${
            paused ? "bg-amber-500" : rate ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"
          }`}
          aria-hidden
        />
        <span>
          {paused
            ? "Indexing paused · maintenance and development"
            : "Live from the indexing dashboard"}
        </span>
        <ArrowUpRight className="h-3 w-3 shrink-0 opacity-60 transition-opacity group-hover:opacity-100" aria-hidden />
      </a>

      {/* Operator 2026-10-11: say why the rate is lower than usual -- part of the fleet is re-reading
          the index while our newest model is trained -- and point to where that model is introduced.
          Kept to plain words: no model names, sizes or dates (Model Fab confidentiality rules). */}
      <p className="max-w-lg text-center text-[11px] leading-relaxed text-foreground/55">
        Indexing is running slower than usual while we train our latest model.{" "}
        <a
          href="https://askfinz.com/model-fab"
          className="inline-flex items-center gap-0.5 text-foreground/75 underline decoration-foreground/25 underline-offset-2 transition-colors hover:text-foreground"
        >
          Meet it in Model Fab
          <ArrowUpRight className="h-3 w-3 shrink-0" aria-hidden />
        </a>
      </p>
    </div>
  );
}

export function SourcesMarquee({ initialRate }: { initialRate?: Rate | null } = {}) {
  /* Every domain seen so far, newest first, one entry per LABEL. */
  const [pool, setPool] = useState<string[]>(FALLBACK);
  const [live, setLive] = useState(false);
  /* Where the visible window starts. Advances only when new domains arrive, so
     the marquee is not restarted for the sake of it. */
  const [offset, setOffset] = useState(0);
  const seen = useRef(new Set(FALLBACK.map(labelOf)));

  useEffect(() => {
    let on = true;

    const pull = () =>
      fetch(DOMAINS_URL, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!on) return;
          const rows: Row[] = d?.last_indexed_domains || [];
          const incoming = rows.map((r) => r.domain).filter(Boolean);
          if (incoming.length < 6) return;

          const firstLive = !live;
          /* Only names never displayed before. If the crawler is working the
             same sites, nothing is new and nothing moves. */
          const fresh = incoming.filter((d2) => !seen.current.has(labelOf(d2)));
          if (!firstLive && !fresh.length) return;

          for (const d2 of incoming) seen.current.add(labelOf(d2));
          setPool((prev) => {
            const base = firstLive ? [] : prev;
            const byLabel = new Map<string, string>();
            for (const d2 of [...incoming, ...base]) {
              const k = labelOf(d2);
              if (!byLabel.has(k)) byLabel.set(k, d2);
            }
            /* Bounded: this is a shop window, not a log. */
            return [...byLabel.values()].slice(0, 120);
          });
          if (firstLive) {
            setLive(true);
            setOffset(0);
          } else {
            /* Walk the window forward so the new arrivals are what you see. */
            setOffset((o) => o + fresh.length);
          }
        })
        .catch(() => {});

    pull();
    const t = setInterval(pull, POLL_MS);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, [live]);

  /* ONE ORDERED WINDOW, SPLIT IN TWO. Slicing the same array at `half` is what
     guarantees the rows share nothing — the old code took two slices that could
     be, and on a short list always were, the same list. */
  const perRow = 8;
  const window: string[] = [];
  for (let i = 0; window.length < perRow * 2 && i < pool.length; i++) {
    window.push(pool[(offset + i) % pool.length]);
  }
  const rowA = window.slice(0, perRow);
  const rowB = window.slice(perRow);
  /* Repeated so the -50% translate loops seamlessly rather than snapping. A row
     with too few chips to fill the viewport is repeated more, not padded from
     the other row. */
  const loop = (xs: string[]) => (xs.length ? [...xs, ...xs, ...xs, ...xs] : xs);

  return (
    <div className="relative">
      <div className="flex items-center justify-center gap-2 mb-6">
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/55">
          {live ? "Sources being read right now" : "Sources indexed"}
        </span>
      </div>

      <div className="relative overflow-hidden">
        <div className="marquee-row flex gap-3 whitespace-nowrap will-change-transform">
          {loop(rowA).map((d, i) => (
            <Chip key={`a-${i}-${d}`} domain={d} />
          ))}
        </div>
        <div className="marquee-row marquee-row-rev mt-3 flex gap-3 whitespace-nowrap will-change-transform">
          {loop(rowB).map((d, i) => (
            <Chip key={`b-${i}-${d}`} domain={d} />
          ))}
        </div>

        {/* Edge fades — inset-y so they never cover the section below. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-background to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-background to-transparent"
        />
      </div>

      {/* BELOW the chips, outside the overflow-hidden wrapper — the edge fades are inset-y-0 and
          would otherwise sit over the top of these figures. */}
      <ReadRate initial={initialRate} />
    </div>
  );
}
